import type { IUser } from '../auth/authSlice';

import { fetchNinjaCsrf, getCachedNinjaCsrf } from '@/libs/ninja/csrf';
import { BACKEND_SERVER_NINJA } from '@/settings';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { toast } from 'react-toastify';
import { StatusEnum } from '../common-types';

export interface IOrganization {
  id: number;
  name: string;
  slug: string;
  users: IUser[];
  owner: IUser;
  is_active: boolean;
  kw_limit: number;
  created?: string;
  modified?: string;
}

export interface IOrganizationState {
  organizations: IOrganization[];
  selectedOrganization: IOrganization | null;
  status: StatusEnum;
  getStatus: StatusEnum;
  statusText: string | null;
  pendingInvitesBySlug: Record<string, { id: number; email: string; organization_id: number }[]>;
  proxiesBySlug: Record<string, IProxy[]>;
}

export const initialState: IOrganizationState = {
  organizations: [],
  selectedOrganization: null,
  status: StatusEnum.idle,
  getStatus: StatusEnum.idle,
  statusText: null,
  pendingInvitesBySlug: {},
  proxiesBySlug: {},
};

export interface ITokenWithUserId {
  user_id: string;
  token: string;
}

export interface IProxy {
  id: number;
  owner: number;
  name: string;
  address: string;
  created?: string;
  changed?: string;
}

export const getOrganizations = createAsyncThunk(
  'organizations/get',
  async () => {
    const { data: organizations } = await axios.get<IOrganization[]>(
      `${BACKEND_SERVER_NINJA}/organizations`
    );
    return organizations;
  }
);

export const revokeOrgUser = createAsyncThunk(
  'organization/revoke-user',
  async ({ slug, orgUserId }: { slug: string; orgUserId: number }) => {
    await axios.delete(
      `${BACKEND_SERVER_NINJA}/organizations/${slug}/users/${orgUserId}`
    );
    return { slug, orgUserId };
  }
);

export const addOrganization = createAsyncThunk(
  'organizations/add',
  async (newOrganization: {
    name: string;
    slug: string;
    kw_limit: number;
    owner: IUser;
  }) => {
    const { data: organization } = await axios.post<IOrganization>(
      `${BACKEND_SERVER_NINJA}/organizations`,
      newOrganization
    );

    const { owner } = newOrganization;

    return { ...organization, owner: owner, users: [owner] };
  }
);

export const deleteOrganization = createAsyncThunk(
  'organizations/delete',
  async (slug: string | number) => {
    axios.delete(`${BACKEND_SERVER_NINJA}/organizations/` + slug);
    return slug;
  }
);

export const updateOrganization = createAsyncThunk(
  'organizations/update',
  async (newOrganization: {
    name?: string;
    slug: string;
    kw_limit?: number;
    is_active?: boolean;
  }) => {
    const { data: organization } = await axios.patch<IOrganization>(
      `${BACKEND_SERVER_NINJA}/organizations/` + newOrganization.slug + '/',
      newOrganization
    );
    return organization;
  }
);

export const inviteUser = createAsyncThunk(
  'organization/invite',
  async ({ slug, email }: { slug: string; email: string }) => {
    const csrf = getCachedNinjaCsrf() ?? (await fetchNinjaCsrf());
    const { data } = await axios.post(
      `${BACKEND_SERVER_NINJA}/organizations/${slug}/invitations`,
      { email },
      { withCredentials: true, headers: { 'X-CSRFTOKEN': csrf } }
    );
    return data as { id: number; email: string; organization_id: number };
  }
);

export const checkInvite = createAsyncThunk(
  'organization/check-invite',
  async ({ user_id, token }: ITokenWithUserId) => {
    const { data } = await axios.get(
      `${BACKEND_SERVER_NINJA}/invitations/${user_id}/${token}`,
      { withCredentials: true }
    );
    const email = typeof data === 'string' ? data : (data?.email as string);
    return { email };
  }
);

export const listInvites = createAsyncThunk(
  'organization/list-invites',
  async ({ slug }: { slug: string }) => {
    const { data } = await axios.get(
      `${BACKEND_SERVER_NINJA}/organizations/${slug}/invitations`,
      { withCredentials: true }
    );
    return { slug, invites: data as { id: number; email: string; organization_id: number }[] };
  }
);

export const resendInvite = createAsyncThunk(
  'organization/resend-invite',
  async ({ slug, inviteId }: { slug: string; inviteId: number }) => {
    const csrf = getCachedNinjaCsrf() ?? (await fetchNinjaCsrf());
    const { data } = await axios.post(
      `${BACKEND_SERVER_NINJA}/organizations/${slug}/invitations/${inviteId}/send_invite`,
      {},
      { withCredentials: true, headers: { 'X-CSRFTOKEN': csrf } }
    );
    return data as { id: number; email: string; organization_id: number };
  }
);

export const activateInvitation = createAsyncThunk(
  'organization/activate-invitation',
  async ({ slug, inviteId }: { slug: string; inviteId: number }) => {
    const { data } = await axios.get(
      `${BACKEND_SERVER_NINJA}/organizations/${slug}/invitations/${inviteId}/activate`,
      { withCredentials: true }
    );
    return data;
  }
);

export const listProxies = createAsyncThunk(
  'organization/list-proxies',
  async ({ slug }: { slug: string }) => {
    const { data } = await axios.get(
      `${BACKEND_SERVER_NINJA}/organizations/${slug}/proxies`,
      { withCredentials: true }
    );
    return { slug, proxies: data as IProxy[] };
  }
);

export const createProxy = createAsyncThunk(
  'organization/create-proxy',
  async ({ slug, name, address }: { slug: string; name: string; address: string }) => {
    const csrf = getCachedNinjaCsrf() ?? (await fetchNinjaCsrf());
    const { data } = await axios.post(
      `${BACKEND_SERVER_NINJA}/organizations/${slug}/proxies`,
      { name, address },
      { withCredentials: true, headers: { 'X-CSRFTOKEN': csrf } }
    );
    return { slug, proxy: data as IProxy };
  }
);

export const updateProxy = createAsyncThunk(
  'organization/update-proxy',
  async ({ slug, id, name, address }: { slug: string; id: number; name: string; address: string }) => {
    const csrf = getCachedNinjaCsrf() ?? (await fetchNinjaCsrf());
    const { data } = await axios.patch(
      `${BACKEND_SERVER_NINJA}/organizations/${slug}/proxies/${id}`,
      { name, address },
      { withCredentials: true, headers: { 'X-CSRFTOKEN': csrf } }
    );
    return { slug, proxy: data as IProxy };
  }
);

export const deleteProxy = createAsyncThunk(
  'organization/delete-proxy',
  async ({ slug, id }: { slug: string; id: number }) => {
    const csrf = getCachedNinjaCsrf() ?? (await fetchNinjaCsrf());
    await axios.delete(
      `${BACKEND_SERVER_NINJA}/organizations/${slug}/proxies/${id}`,
      { withCredentials: true, headers: { 'X-CSRFTOKEN': csrf } }
    );
    return { slug, id };
  }
);

const organizationSlice = createSlice({
  name: 'organization',
  initialState,
  reducers: {
    setOrganizationsIdle: (state) => {
      state.status = StatusEnum.idle;
      state.statusText = null;
      state.selectedOrganization = null;
      state.organizations = [];
    },
    selectActiveOrganization: (state, { payload }) => {
      localStorage.setItem('activeOgraniationSlug', payload);
      state.selectedOrganization = state.organizations.find(
        ({ slug }) => slug == payload
      ) as IOrganization;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getOrganizations.pending, (state) => {
        state.getStatus = StatusEnum.loading;
      })
      .addCase(getOrganizations.fulfilled, (state, { payload }) => {
        state.getStatus = StatusEnum.succeeded;
        state.organizations = payload;

        if (!state.selectedOrganization) {
          state.selectedOrganization =
            payload.find(
              ({ slug }) =>
                slug == localStorage.getItem('activeOgraniationSlug')
            ) ??
            payload?.[0] ??
            null;
        }
      })
      .addCase(getOrganizations.rejected, (state, { error }) => {
        state.getStatus = StatusEnum.failed;
        if (error.code === 'ERR_BAD_REQUEST') {
          state.statusText = 'No Auth';
        } else {
          state.statusText = error.message ?? null;
        }
        toast.error(state.statusText);
      })
      .addCase(addOrganization.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(addOrganization.fulfilled, (state, { payload }) => {
        state.status = StatusEnum.idle;
        state.organizations = [...state.organizations, payload];
        toast.success('Команда успешно создана');
      })
      .addCase(addOrganization.rejected, (state, action) => {
        state.status = StatusEnum.failed;
        state.statusText = action.error.message ?? null;
        toast.error(state.statusText);
      })
      .addCase(deleteOrganization.fulfilled, (state, { payload }) => {
        state.organizations = state.organizations.filter(
          ({ slug }) => slug != payload
        );
        toast.success('Команда успешно удалена');
      })
      .addCase(updateOrganization.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(updateOrganization.fulfilled, (state, { payload }) => {
        state.status = StatusEnum.idle;

        const organizationIndex = state.organizations.findIndex(
          ({ slug }) => slug == payload.slug
        );
        if (organizationIndex !== -1) {
          state.organizations[organizationIndex] = payload;
        }

        toast.success('Команда успешно обновлена');
      })
      .addCase(updateOrganization.rejected, (state, action) => {
        state.status = StatusEnum.failed;
        state.statusText = action.error.message ?? null;
        toast.error(state.statusText);
      })
      .addCase(inviteUser.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(inviteUser.fulfilled, (state, action) => {
        state.status = StatusEnum.succeeded;
        toast.success('Приглашение успешное отправлено');
        const slug = action.meta.arg.slug;
        const invite = action.payload;
        if (invite && invite.id) {
          const list =
            state.pendingInvitesBySlug[slug] ??
            (state.pendingInvitesBySlug[slug] = []);
          const exists = list.some(({ id }) => id === invite.id);
          if (!exists) list.push(invite);
        }
      })
      .addCase(inviteUser.rejected, (state, action) => {
        state.status = StatusEnum.failed;
        state.statusText = action.error.message ?? null;
        toast.error(state.statusText);
      })
      .addCase(listInvites.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(listInvites.fulfilled, (state, action) => {
        state.status = StatusEnum.succeeded;
        const { slug, invites } = action.payload;
        state.pendingInvitesBySlug[slug] = invites;
      })
      .addCase(listInvites.rejected, (state, action) => {
        state.status = StatusEnum.failed;
        state.statusText = action.error.message ?? null;
        toast.error(state.statusText);
      })
      .addCase(resendInvite.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(resendInvite.fulfilled, (state, action) => {
        state.status = StatusEnum.succeeded;
        toast.success('Приглашение повторно отправлено');
        const slug = action.meta.arg.slug;
        const updated = action.payload;
        const list = state.pendingInvitesBySlug[slug];
        if (list) {
          const index = list.findIndex(({ id }) => id === updated.id);
          if (index >= 0) list[index] = updated;
        }
      })
      .addCase(resendInvite.rejected, (state, action) => {
        state.status = StatusEnum.failed;
        state.statusText = action.error.message ?? null;
        toast.error(state.statusText);
      })
      .addCase(listProxies.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(listProxies.fulfilled, (state, action) => {
        state.status = StatusEnum.succeeded;
        const { slug, proxies } = action.payload;
        state.proxiesBySlug[slug] = proxies;
      })
      .addCase(listProxies.rejected, (state, action) => {
        state.status = StatusEnum.failed;
        state.statusText = action.error.message ?? null;
        toast.error(state.statusText);
      })
      .addCase(createProxy.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(createProxy.fulfilled, (state, action) => {
        state.status = StatusEnum.succeeded;
        const { slug, proxy } = action.payload;
        const list = state.proxiesBySlug[slug] ?? (state.proxiesBySlug[slug] = []);
        const existsIndex = list.findIndex(({ id }) => id === proxy.id);
        if (existsIndex >= 0) list[existsIndex] = proxy; else list.push(proxy);
        toast.success('Прокси успешно добавлен');
      })
      .addCase(createProxy.rejected, (state, action) => {
        state.status = StatusEnum.failed;
        state.statusText = action.error.message ?? null;
        toast.error(state.statusText);
      })
      .addCase(updateProxy.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(updateProxy.fulfilled, (state, action) => {
        state.status = StatusEnum.succeeded;
        const { slug, proxy } = action.payload;
        const list = state.proxiesBySlug[slug];
        if (list) {
          const index = list.findIndex(({ id }) => id === proxy.id);
          if (index >= 0) list[index] = proxy;
        }
        toast.success('Прокси успешно обновлён');
      })
      .addCase(updateProxy.rejected, (state, action) => {
        state.status = StatusEnum.failed;
        state.statusText = action.error.message ?? null;
        toast.error(state.statusText);
      })
      .addCase(deleteProxy.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(deleteProxy.fulfilled, (state, action) => {
        state.status = StatusEnum.succeeded;
        const { slug, id } = action.payload;
        const list = state.proxiesBySlug[slug];
        if (list) {
          state.proxiesBySlug[slug] = list.filter((p) => p.id !== id);
        }
        toast.success('Прокси успешно удалён');
      })
      .addCase(deleteProxy.rejected, (state, action) => {
        state.status = StatusEnum.failed;
        state.statusText = action.error.message ?? null;
        toast.error(state.statusText);
      })
      .addCase(revokeOrgUser.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(revokeOrgUser.fulfilled, (state, action) => {
        state.status = StatusEnum.succeeded;
        const { slug, orgUserId } = action.payload;
        const orgIndex = state.organizations.findIndex(({ slug: s }) => s === slug);
        const org = state.organizations[orgIndex];
        if (org) {
          const users = org.users || [];
          org.users = users.filter(({ id }) => id !== orgUserId);
        }
        toast.success('Доступ пользователя отозван');
      })
      .addCase(revokeOrgUser.rejected, (state, action) => {
        state.status = StatusEnum.failed;
        state.statusText = action.error.message ?? null;
        toast.error(state.statusText);
      });
  },
});

export const { setOrganizationsIdle, selectActiveOrganization } =
  organizationSlice.actions;

export default organizationSlice.reducer;
